import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
} from '@mui/material';
import { EditOutlined as EditIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../apiBase';

export default function PageMaster() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const pagePerm = permissionsMap.page_master || {};
  const canCreate = isSuperAdmin || Boolean(pagePerm.can_create);
  const canUpdate = isSuperAdmin || Boolean(pagePerm.can_update);
  const canDelete = isSuperAdmin || Boolean(pagePerm.can_delete);

  const [pageKey, setPageKey] = useState('');
  const [label, setLabel] = useState('');
  const [route, setRoute] = useState('');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState('success');
  const [toastMessage, setToastMessage] = useState('');
  const [routeMenuAnchor, setRouteMenuAnchor] = useState(null);

  const routeOptions = [
    { path: '/dashboard', label: 'Dashboard', pageKey: 'dashboard' },
    { path: '/articles', label: 'Article Creation', pageKey: 'article_creation' },
    { path: '/vendors', label: 'Vendor Management', pageKey: 'vendor_master' },
    { path: '/vendor-followup', label: 'Vendor Follow Up', pageKey: 'vendor_followup' },
    { path: '/manual-head-office-transaction', label: 'Manual Head Office Transaction', pageKey: 'manual_head_office_transaction' },
    { path: '/vendor-followup-report', label: 'Daily Vendor Followup Report', pageKey: 'vendor_followup_report' },
    { path: '/head-office-report', label: 'Daily Head Office Transaction Report', pageKey: 'head_office_report' },
    { path: '/code-purpose', label: 'Code To Code Purpose', pageKey: 'code_to_code_purpose' },
    { path: '/transaction-types', label: 'Transaction Types', pageKey: 'transaction_types' },
    { path: '/rolemaster', label: 'Role Master', pageKey: 'role_master' },
    { path: '/pagemaster', label: 'Page Master', pageKey: 'page_master' },
    { path: '/user-permissions', label: 'User Permissions', pageKey: 'user_permissions' },
    { path: '/users', label: 'Users', pageKey: 'users' },
    { path: '/tasks', label: 'Task Management', pageKey: 'task_management' },
    { path: '/applications', label: 'User Application Report', pageKey: 'applications' },
    { path: '/applications/add', label: 'User Application Form', pageKey: 'applications_add' },
    { path: '/hardcopy-sheet', label: 'Hardcopy Sheet Management', pageKey: 'hardcopy_sheet_management' },
    { path: '/hardcopy-sheet-report', label: 'Hardcopy Sheet Report', pageKey: 'hardcopy_sheet_report' },
    { path: '/multi-hardcopy-sheet', label: 'Multi Hardcopy Sheet', pageKey: 'multi_hardcopy_sheet' },
    { path: '/multi-hardcopy-sheet-report', label: 'Multi Hardcopy Sheet Report', pageKey: 'multi_hardcopy_sheet_report' },
    { path: '/datacenter', label: 'Data Center', pageKey: 'datacenter' },
    { path: '/requests', label: 'Requests', pageKey: 'requests' },
    { path: '/edit-requests', label: 'Edit Requests', pageKey: 'edit_requests' },
    { path: '/rtgs', label: 'RTGS Details', pageKey: 'rtgs' },
    { path: '/calendar', label: 'Calendar', pageKey: 'calendar' },
    { path: '/grid', label: 'Grid', pageKey: 'grid' },
  ];

  const generatePageKeyFromLabel = (value) => {
    if (!value) return '';
    return value
      .toLowerCase()
      .trim()
      .replace(/[^\w]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const showToast = (severity, message) => {
    setToastSeverity(severity);
    setToastMessage(message);
    setToastOpen(true);
  };

  const loadPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to load pages');
      }
      setPages(Array.isArray(data.pages) ? data.pages : []);
    } catch (error) {
      showToast('error', error.message || 'Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      if (!canUpdate) {
        return;
      }
    } else if (!canCreate) {
      return;
    }

    const trimmedKey = pageKey.trim();
    const trimmedLabel = label.trim();
    const trimmedRoute = route.trim();
    if (!trimmedKey || !trimmedLabel || !trimmedRoute) {
      showToast('error', 'Please fill all fields');
      return;
    }

    const selectedRoute = routeOptions.find((opt) => opt.path === trimmedRoute);
    const finalPageKey =
      (selectedRoute && selectedRoute.pageKey) ||
      trimmedKey ||
      generatePageKeyFromLabel(trimmedLabel) ||
      generatePageKeyFromLabel(trimmedRoute);

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editingId ? `${API_BASE_URL}/api/pages/${editingId}` : `${API_BASE_URL}/api/pages`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          page_key: finalPageKey,
          label: trimmedLabel,
          route: trimmedRoute,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || (editingId ? 'Failed to update page' : 'Failed to create page'));
      }

      if (editingId) {
        setPages((prev) => prev.map((p) => (p.id === data.page.id ? data.page : p)));
        showToast('success', 'Page updated successfully');
      } else {
        setPages((prev) => [data.page, ...prev]);
        showToast('success', 'Page created successfully');
      }

      setPageKey('');
      setLabel('');
      setRoute('');
      setEditingId(null);
    } catch (error) {
      showToast(
        'error',
        error.message || (editingId ? 'Failed to update page' : 'Failed to create page')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (page) => {
    if (!canUpdate) {
      showToast('error', 'You do not have update permission for this page');
      return;
    }
    setEditingId(page.id);
    setPageKey(page.page_key || '');
    setLabel(page.label || '');
    setRoute(page.route || '');
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = async (id) => {
    if (!canDelete || !id) {
      return;
    }
    setDeletingId(id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/pages/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete page');
      }
      setPages((prev) => prev.filter((p) => p.id !== id));
      showToast('success', 'Page deleted');
    } catch (error) {
      showToast('error', error.message || 'Failed to delete page');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Layout>
      {/* <Typography variant="h5" sx={{ fontWeight: 500, mb: 2 }}>
        Page Master
      </Typography> */}
      <Paper sx={{ p: 3, borderRadius: 1, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 520 }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              label="Route"
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              fullWidth
            />
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                setPageKey('');
                setLabel('');
                setRoute('');
                setRouteMenuAnchor(e.currentTarget);
              }}
              sx={{ mb: 0.5, whiteSpace: 'nowrap' }}
            >
              Browse Page
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -1 }}>
            Example: /vendor-master
          </Typography>
          <TextField
            label="Page Key"
            value={pageKey}
            onChange={(e) => setPageKey(e.target.value)}
            helperText="Example: vendor_master, page_master"
            fullWidth
          />
          <TextField
            label="Page Name"
            value={label}
            onChange={(e) => {
              const value = e.target.value;
              setLabel(value);
              if (!pageKey) {
                setPageKey(generatePageKeyFromLabel(value));
              }
            }}
            fullWidth
          />
          <Menu
            anchorEl={routeMenuAnchor}
            open={Boolean(routeMenuAnchor)}
            onClose={() => setRouteMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {routeOptions.map((opt) => (
              <MenuItem
                key={opt.path}
                onClick={() => {
                  setRoute(opt.path);
                  if (!label) {
                    setLabel(opt.label);
                  }
                  if (!pageKey) {
                    setPageKey(opt.pageKey || generatePageKeyFromLabel(opt.label));
                  }
                  setRouteMenuAnchor(null);
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{opt.label}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {opt.path}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={(editingId ? !canUpdate : !canCreate) || saving}
            >
              {saving ? 'Saving...' : editingId ? 'Update' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1">Pages</Typography>
          <Button size="small" onClick={loadPages} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>#</TableCell>
                <TableCell>Page Key</TableCell>
                <TableCell>Page Name</TableCell>
                <TableCell>Route</TableCell>
                <TableCell sx={{ width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pages.map((page) => (
                <TableRow key={page.id}>
                  <TableCell>{page.id}</TableCell>
                  <TableCell>{page.page_key}</TableCell>
                  <TableCell>{page.label}</TableCell>
                  <TableCell>{page.route}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      sx={{ mr: 0.5 }}
                      disabled={!canUpdate}
                      onClick={() => handleEdit(page)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={!canDelete || deletingId === page.id}
                      onClick={() => handleDelete(page.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {pages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                    No pages added yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastSeverity} sx={{ width: '100%' }}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
